import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Radio, Play, Pause, Volume2, Users, Heart, Share2,
  History, ExternalLink, Music, Headphones, MessageCircle,
  SkipForward, CloudRain, Tv, Clock, LogIn, X, Send,
  ArrowLeft, Link2, Settings, User, RefreshCw, Search, Disc3,
  Bell, BellOff, UserMinus, Download
} from "lucide-react";
import { useAuth, BACKEND_URL } from "../context/AuthContext";
import audioBus from "../services/AudioBus";
import { useStreamResilience } from "../hooks/useStreamResilience";
import SEO from "../components/SEO";
import CanvasVisualizer from "../components/player/CanvasVisualizer";
import PrerollSplash from "../components/player/PrerollSplash";
import NewsTicker from "../components/player/NewsTicker";
import SmartVideoPlayer from "../components/station/SmartVideoPlayer";
import FFTVisualizer from "../components/player/FFTVisualizer";
const WhiteLabelPlayer = ({ overrideSlug }) => {
  const params = useParams();
  const slug = overrideSlug || params.slug;
  const isEmbedMode = new URLSearchParams(window.location.search).get("embed") === "true";
  const { user, token, login, register } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(!isEmbedMode);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [analyzerData, setAnalyzerData] = useState(new Array(32).fill(5));
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [activeTab, setActiveTab] = useState("history");
  const [weather, setWeather] = useState(null);
  const [currentTime, setCurrentTime] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [requestText, setRequestText] = useState("");
  const [dedicateText, setDedicateText] = useState("");
  const [dedicateTo, setDedicateTo] = useState("");
  const [requestSuccess, setRequestSuccess] = useState("");
  const [showChatLogin, setShowChatLogin] = useState(false);
  const [chatAuthMode, setChatAuthMode] = useState("login");
  const [chatAuthEmail, setChatAuthEmail] = useState("");
  const [chatAuthPassword, setChatAuthPassword] = useState("");
  const [chatAuthName, setChatAuthName] = useState("");
  const [chatAuthError, setChatAuthError] = useState("");
  const [chatAuthLoading, setChatAuthLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followEmail, setFollowEmail] = useState("");
  const [showFollowEmail, setShowFollowEmail] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showFollowMenu, setShowFollowMenu] = useState(false);
  const [showPlsMenu, setShowPlsMenu] = useState(false);
  const [notifyEnabled, setNotifyEnabled] = useState(true);
  const [followedEmail, setFollowedEmail] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [myPoints, setMyPoints] = useState({ points: 0, minutes_listened: 0, rank: null });
  const [showOnDemand, setShowOnDemand] = useState(false);
  const [liveElapsed, setLiveElapsed] = useState("");
  const [onDemandTracks, setOnDemandTracks] = useState([]);
  const [onDemandLoading, setOnDemandLoading] = useState(false);
  const [onDemandSearch, setOnDemandSearch] = useState("");
  const [onDemandPlaying, setOnDemandPlaying] = useState(null);
  const [onDemandTimeLeft, setOnDemandTimeLeft] = useState(0);
  const onDemandAudioRef = useRef(null);
  const onDemandTimerRef = useRef(null);
  const onDemandCountdownRef = useRef(null);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyzerRef = useRef(null);
  const animationRef = useRef(null);
  const audioBusHandle = useRef(null);
  const { isBuffering } = useStreamResilience(audioRef);

  const handleChatAuth = async (e) => {
    e.preventDefault();
    setChatAuthError("");
    setChatAuthLoading(true);
    try {
      if (chatAuthMode === "login") {
        await login(chatAuthEmail, chatAuthPassword);
      } else {
        await register(chatAuthName, chatAuthEmail, chatAuthPassword);
      }
      setShowChatLogin(false);
    } catch (err) {
      setChatAuthError(err.message || "Authentication failed");
    } finally {
      setChatAuthLoading(false);
    }
  };

  const handleChatGoogleSignIn = () => {
    const redirectUrl = window.location.href;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // On-Demand functions
  const fetchOnDemand = async (search = "") => {
    if (!config) return;
    setOnDemandLoading(true);
    try {
      const stationId = config.azuracast_station_id || config.station_id;
      const q = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await fetch(`${BACKEND_URL}/api/ondemand/${stationId}${q}`);
      if (res.ok) {
        const data = await res.json();
        setOnDemandTracks(data.tracks || []);
      }
    } catch (err) {
      console.error("On-demand error:", err);
    } finally {
      setOnDemandLoading(false);
    }
  };

  const playOnDemandTrack = (track) => {
    if (onDemandPlaying?.track_id === track.track_id) {
      // Toggle pause/play
      if (onDemandAudioRef.current) {
        if (onDemandAudioRef.current.paused) {
          if (audioBusHandle.current) audioBusHandle.current.claim();
          onDemandAudioRef.current.play();
        } else {
          onDemandAudioRef.current.pause();
        }
      }
      return;
    }
    // Stop live stream if playing
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    // Clear any existing timer
    clearTimeout(onDemandTimerRef.current);
    clearInterval(onDemandCountdownRef.current);

    setOnDemandPlaying(track);
    setOnDemandTimeLeft(60);
    if (onDemandAudioRef.current) {
      if (audioBusHandle.current) audioBusHandle.current.claim();
      onDemandAudioRef.current.src = track.download_url;
      onDemandAudioRef.current.play();
    }

    // 60-second preview timer — auto-switch back to live
    onDemandTimerRef.current = setTimeout(() => {
      clearInterval(onDemandCountdownRef.current);
      if (onDemandAudioRef.current) {
        onDemandAudioRef.current.pause();
        onDemandAudioRef.current.src = "";
      }
      setOnDemandPlaying(null);
      setOnDemandTimeLeft(0);
      // Resume live stream
      if (audioRef.current) {
        if (audioBusHandle.current) audioBusHandle.current.claim();
        audioRef.current.play().catch(() => {});
        setIsPlaying(true);
      }
    }, 60000);

    // Countdown interval for UI
    onDemandCountdownRef.current = setInterval(() => {
      setOnDemandTimeLeft(prev => {
        if (prev <= 1) { clearInterval(onDemandCountdownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const stopOnDemand = () => {
    clearTimeout(onDemandTimerRef.current);
    clearInterval(onDemandCountdownRef.current);
    if (onDemandAudioRef.current) {
      onDemandAudioRef.current.pause();
      onDemandAudioRef.current.src = "";
    }
    setOnDemandPlaying(null);
    setOnDemandTimeLeft(0);
  };

  // Fetch config
  const fetchConfig = async (showIndicator = false) => {
    if (showIndicator) setRefreshing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/whitelabel/player/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setElapsed(data.now_playing?.elapsed || 0);
      }
    } catch (err) {
      console.error("Failed to fetch player config:", err);
    } finally {
      setLoading(false);
      if (showIndicator) setTimeout(() => setRefreshing(false), 600);
    }
  };

  useEffect(() => {
    fetchConfig();
    const interval = setInterval(() => fetchConfig(false), 10000);
    return () => {
      clearInterval(interval);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [slug]);

  // Fetch follow status
  useEffect(() => {
    if (!slug) return;
    const authToken = token || localStorage.getItem("token");
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
    fetch(`${BACKEND_URL}/api/stations/${slug}/followers`).then(r => r.json()).then(d => setFollowerCount(d.follower_count || 0)).catch(() => {});
    if (authToken) {
      fetch(`${BACKEND_URL}/api/stations/${slug}/is-following`, { headers }).then(r => r.json()).then(d => {
        setIsFollowing(d.following);
        setNotifyEnabled(d.notify ?? true);
      }).catch(() => {});
    }
  }, [slug, token]);

  const requestPushPermission = async (stationId, authToken, email) => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return;
      const reg = await navigator.serviceWorker.register("/sw-push.js");
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) return;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const headers = { "Content-Type": "application/json" };
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
      await fetch(`${BACKEND_URL}/api/push/subscribe`, {
        method: "POST", headers,
        body: JSON.stringify({ station_id: stationId, subscription: sub.toJSON(), email }),
      });
    } catch (e) { console.error("Push subscribe error:", e); }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  };

  const handleFollow = async () => {
    // If already following, toggle the preferences menu
    if (isFollowing) { setShowFollowMenu(m => !m); return; }
    setFollowLoading(true);
    const authToken = token || localStorage.getItem("token");
    const isLoggedIn = !!(authToken || user);
    if (!isLoggedIn && !followEmail) { setShowFollowEmail(true); setFollowLoading(false); return; }
    const headers = { "Content-Type": "application/json" };
    if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
    const body = { station_id: slug };
    if (!authToken && user?.email) body.email = user.email;
    else if (!authToken && followEmail) body.email = followEmail;
    try {
      const res = await fetch(`${BACKEND_URL}/api/stations/follow`, { method: "POST", headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (data.status === "success" || data.status === "already_following") {
        setIsFollowing(true);
        setNotifyEnabled(true);
        setFollowerCount(data.follower_count ?? followerCount + 1);
        if (!authToken && followEmail) setFollowedEmail(followEmail);
        setShowFollowEmail(false);
        setFollowEmail("");
        // Request push notification permission
        requestPushPermission(slug, authToken, body.email);
      }
    } catch (e) { console.error(e); }
    finally { setFollowLoading(false); }
  };

  const handleUnfollow = async () => {
    setFollowLoading(true);
    const authToken = token || localStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
    const body = { station_id: slug };
    if (!authToken && followedEmail) body.email = followedEmail;
    try {
      const res = await fetch(`${BACKEND_URL}/api/stations/unfollow`, { method: "POST", headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (data.status === "success") {
        setIsFollowing(false);
        setFollowerCount(data.follower_count ?? Math.max(0, followerCount - 1));
        setShowFollowMenu(false);
        setFollowedEmail("");
      }
    } catch (e) { console.error(e); }
    finally { setFollowLoading(false); }
  };

  const handleToggleNotify = async () => {
    const authToken = token || localStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
    const newVal = !notifyEnabled;
    const body = { station_id: slug, notify: newVal };
    if (!authToken && followedEmail) body.email = followedEmail;
    try {
      const res = await fetch(`${BACKEND_URL}/api/stations/notify`, { method: "PATCH", headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (data.status === "success") setNotifyEnabled(newVal);
    } catch (e) { console.error(e); }
  };

  // Close follow menu on outside click
  useEffect(() => {
    if (!showFollowMenu) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="follow-prefs-panel"]') && !e.target.closest('[data-testid="wl-follow-btn"]')) {
        setShowFollowMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showFollowMenu]);


  // Fetch weather
  useEffect(() => {
    if (!config?.show_weather) return;
    const fetchWeather = async () => {
      try {
        const city = config.weather_city;
        const lat = config.weather_lat;
        const lon = config.weather_lon;
        let url = `${BACKEND_URL}/api/weather`;
        if (city) {
          url += `?city=${encodeURIComponent(city)}`;
        } else if (lat && lon) {
          url += `?lat=${lat}&lon=${lon}`;
        }
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          if (!data.error) setWeather(data);
        }
      } catch (err) {
        console.error("Weather error:", err);
      }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 1800000);
    return () => clearInterval(interval);
  }, [config?.show_weather, config?.weather_city, config?.weather_lat, config?.weather_lon]);

  // Loyalty heartbeat + leaderboard
  useEffect(() => {
    if (!slug || !config?.show_top_fans) return;
    const fetchLeaderboard = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/whitelabel/loyalty/leaderboard/${slug}`);
        if (res.ok) setLeaderboard(await res.json());
      } catch (err) { console.warn("Leaderboard fetch failed:", err.message); }
    };
    fetchLeaderboard();
    const lbInterval = setInterval(fetchLeaderboard, 30000);

    // Heartbeat — send every 60s while playing (earns 1 point/min)
    const heartbeatInterval = setInterval(() => {
      if (!user || !audioRef.current || audioRef.current.paused) return;
      const t = localStorage.getItem('token');
      if (!t) return;
      fetch(`${BACKEND_URL}/api/whitelabel/loyalty/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${t}` },
        body: JSON.stringify({ slug }),
      }).then(r => r.json()).then(d => setMyPoints(p => ({ ...p, points: d.points, minutes_listened: d.minutes }))).catch(() => {});
    }, 60000);

    // Fetch my points
    if (user) {
      const t = localStorage.getItem('token');
      fetch(`${BACKEND_URL}/api/whitelabel/loyalty/me/${slug}`, {
        headers: { 'Authorization': `Bearer ${t}` },
      }).then(r => r.json()).then(setMyPoints).catch(() => {});
    }

    return () => { clearInterval(lbInterval); clearInterval(heartbeatInterval); };
  }, [slug, config?.show_top_fans, user]);

  // Clock
  useEffect(() => {
    if (!config?.show_clock) return;
    const tz = config.timezone || "America/Chicago";
    const update = () => {
      try {
        setCurrentTime(
          new Date().toLocaleTimeString("en-US", {
            timeZone: tz,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
        );
      } catch {
        setCurrentTime(new Date().toLocaleTimeString());
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [config?.show_clock, config?.timezone]);

  // Track elapsed time
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(interval);
  }, [isPlaying]);

  const setupAnalyzer = useCallback(() => {
    if (!audioRef.current || audioContextRef.current) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyzer = ctx.createAnalyser();
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyzer);
      analyzer.connect(ctx.destination);
      analyzer.fftSize = 256;
      audioContextRef.current = ctx;
      analyzerRef.current = analyzer;
      const buf = new Uint8Array(analyzer.frequencyBinCount);
      const animate = () => {
        analyzer.getByteFrequencyData(buf);
        setAnalyzerData(Array.from(buf).map((v) => Math.max(5, (v / 255) * 100)));
        animationRef.current = requestAnimationFrame(animate);
      };
      animate();
    } catch (err) {
      console.error("Analyzer error:", err);
    }
  }, []);

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      if (isEmbedMode && window.parent !== window) {
        window.parent.postMessage({ type: 'djmc_analytics', event: 'pause' }, '*');
      }
    } else {
      if (!audioBusHandle.current) {
        audioBusHandle.current = audioBus.register('whitelabel-player', {
          pause: () => {
            if (audioRef.current) audioRef.current.pause();
            if (onDemandAudioRef.current) { onDemandAudioRef.current.pause(); onDemandAudioRef.current.src = ""; }
            setIsPlaying(false);
            setOnDemandPlaying(null);
          }
        });
      }
      audioBusHandle.current.claim();
      try {
        await audioRef.current.play();
        setupAnalyzer();
      } catch (err) {
        console.warn("Play failed with crossOrigin, retrying:", err);
        audioRef.current.removeAttribute("crossorigin");
        audioRef.current.src = config.stream_url;
        await audioRef.current.play();
      }
      if (isEmbedMode && window.parent !== window) {
        window.parent.postMessage({ type: 'djmc_analytics', event: 'play' }, '*');
      }
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  };

  const formatLiveDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
    return `${s}s`;
  };

  // Live stream elapsed timer
  useEffect(() => {
    const live = config?.now_playing?.live || {};
    const bStart = live.broadcast_start ? new Date(live.broadcast_start * 1000) : null;
    if (!bStart || live.is_live !== true) { setLiveElapsed(""); return; }
    const tick = () => {
      const diff = Math.floor((Date.now() - bStart.getTime()) / 1000);
      setLiveElapsed(diff > 0 ? formatLiveDuration(diff) : "");
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [config]);

  const timeAgo = (ts) => {
    const d = Date.now() / 1000 - ts;
    if (d < 60) return "Just now";
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    return `${Math.floor(d / 86400)}d ago`;
  };

  /* Loading */
  if (loading)
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Radio className="w-12 h-12 animate-pulse" style={{ color: "#8B5CF6" }} />
      </div>
    );

  /* Not found */
  if (!config)
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center max-w-lg">
          <div className="relative mb-8">
            <span className="text-[120px] sm:text-[160px] font-black text-white/[0.03] leading-none select-none block">404</span>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 bg-purple-500/10 border border-purple-500/20 rounded-full flex items-center justify-center">
                <Radio className="w-10 h-10 text-purple-400" />
              </div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2" data-testid="player-not-found">Station Not Found</h1>
          <p className="text-white/50 mb-6 text-sm">This station doesn't exist or has been deactivated.</p>
          <a href="/" className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold transition text-sm">
            <ArrowLeft size={16} /> Back to DJsandMCMedia
          </a>
        </div>
      </div>
    );

  /* Preroll Splash */
  if (showSplash)
    return <PrerollSplash config={config} onEnter={() => setShowSplash(false)} />;

  const pc = config.primary_color || "#8B5CF6";
  const sc = config.secondary_color || "#22C55E";
  const song = config.now_playing?.song || {};
  const duration = config.now_playing?.duration || 240;
  const songHistory = config.now_playing?.song_history || [];
  const playingNext = config.now_playing?.playing_next || {};
  const listeners = config.now_playing?.listeners || 0;
  const liveInfo = config.now_playing?.live || {};
  const isLiveDJ = liveInfo.is_live === true;
  const broadcastStart = liveInfo.broadcast_start ? new Date(liveInfo.broadcast_start * 1000) : null;

  const tabs = [
    config.show_recently_played && { id: "history", label: "History", icon: History },
    config.show_requests && { id: "request", label: "Request", icon: Music },
    config.show_dedicate && { id: "dedicate", label: "Dedicate", icon: Heart },
    config.show_share && { id: "share", label: "Share", icon: Share2 },
    config.show_top_fans && { id: "topfans", label: "Top Fans", icon: Users },
  ].filter(Boolean);

  const bgType = config.bg_type || "dark";
  const bgColor = config.bg_color || "#050505";
  const bgGradientStart = config.bg_gradient_start || "#050505";
  const bgGradientEnd = config.bg_gradient_end || "#1a1a2e";
  const bgUrl = config.background_url;

  const playerBg = bgType === "image" && bgUrl
    ? "#050505"
    : bgType === "solid"
    ? bgColor
    : bgType === "gradient"
    ? `linear-gradient(135deg, ${bgGradientStart}, ${bgGradientEnd})`
    : "#050505";

  return (
    <div className="min-h-screen" style={{ background: playerBg, fontFamily: "'Inter', sans-serif" }} data-testid="whitelabel-player">
      <SEO
        title={config.player_name || "Premium Player"}
        description={`Listen to ${config.player_name || 'this station'} live. ${config.tagline || 'Powered by DJsandMCMedia™'}`}
        image={config.logo_url || "/og-image.png"}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "RadioStation",
          "name": config.player_name,
          "url": window.location.href,
          "description": config.tagline,
        }}
      />
      {config.custom_css && <style>{config.custom_css}</style>}
      <style>{`
        .glass { backdrop-filter: blur(16px); background: #12121299; border: 1px solid #ffffff1a; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 18px; height: 18px; border-radius: 50%; background: white; cursor: pointer; box-shadow: 0 1px 4px rgba(0,0,0,0.4); position: relative; z-index: 2; }
        input[type=range]::-moz-range-thumb { width: 18px; height: 18px; border-radius: 50%; background: white; cursor: pointer; border: none; box-shadow: 0 1px 4px rgba(0,0,0,0.4); }
        select option { background: #1E1E1E; color: white; }
      `}</style>

      {/* Background layer */}
      {bgType === "image" && bgUrl ? (
        <div className="fixed inset-0" style={{
          backgroundImage: `url(${bgUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}>
          <div className="absolute inset-0 bg-black/60" />
        </div>
      ) : bgType === "dark" ? (
        <div className="fixed inset-0" style={{
          background: `radial-gradient(ellipse at 30% 30%, ${pc}15 0%, transparent 50%), radial-gradient(ellipse at 70% 70%, ${sc}10 0%, transparent 50%), #050505`
        }} />
      ) : null}

      {/* Header */}
      {/* Embed Mode Top Banner */}
      {isEmbedMode && (
        <div className="relative z-30 px-4 py-2 text-center" style={{ background: `linear-gradient(90deg, ${pc}20, ${sc}20)`, borderBottom: `1px solid ${pc}30` }}>
          <a href="https://djsandmcmedia.com" target="_blank" rel="noopener noreferrer"
            className="text-xs font-medium transition hover:opacity-80" style={{ color: pc }}
            data-testid="embed-cta-banner">
            Launch your own station on DJsandMCMedia →
          </a>
        </div>
      )}

      <header className="relative z-30 px-4 sm:px-6 py-4 border-b" style={{ borderColor: "#27272a" }}>
        {/* Top row: Logo + Name + Listeners/Buttons */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {config.logo_url ? (
              <img src={config.logo_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                style={{ filter: `drop-shadow(0 0 8px ${pc}40)` }} />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${pc}, ${sc})` }}>
                <Radio className="w-5 h-5 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="font-bold text-white truncate" style={{ fontFamily: "Teko, sans-serif", fontSize: "1.4rem", lineHeight: "1.2" }}
                data-testid="wl-station-name">
                {config.player_name || "Live Radio"}
              </h1>
              {config.tagline && <p className="text-xs truncate" style={{ color: "#A1A1AA" }}>{config.tagline}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {config.show_ondemand && (
            <button
              onClick={() => { setShowOnDemand(!showOnDemand); if (!showOnDemand && onDemandTracks.length === 0) fetchOnDemand(); }}
              data-testid="wl-ondemand-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition hover:bg-white/10 text-sm font-medium"
              style={{ background: showOnDemand ? `${pc}20` : "#121212", border: `1px solid ${showOnDemand ? pc : '#27272a'}`, color: showOnDemand ? pc : '#A1A1AA' }}
              title="On-Demand Library"
            >
              <Disc3 className="w-4 h-4" />
              <span className="hidden sm:inline">On-Demand</span>
            </button>
            )}
            <button
              onClick={() => {
                fetchConfig(true);
                // Reconnect the audio stream
                if (audioRef.current && isPlaying) {
                  if (audioBusHandle.current) audioBusHandle.current.claim();
                  audioRef.current.src = config.stream_url;
                  audioRef.current.load();
                  audioRef.current.play().catch(() => {});
                }
              }}
              data-testid="wl-refresh-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition hover:bg-white/10"
              style={{ background: "#121212", border: "1px solid #27272a" }}
              title="Refresh stream"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} style={{ color: sc }} />
            </button>
            {config.show_listeners && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: "#121212", border: "1px solid #27272a" }}>
                <Users className="w-4 h-4" style={{ color: sc }} />
                <span className="text-white text-sm">{listeners}</span>
              </div>
            )}
            {/* Follow Button */}
            <div className="relative">
              <button
                onClick={handleFollow}
                disabled={followLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition"
                style={{
                  background: isFollowing ? `${pc}20` : "#121212",
                  border: `1px solid ${isFollowing ? pc : '#27272a'}`,
                  color: isFollowing ? pc : '#A1A1AA'
                }}
                data-testid="wl-follow-btn"
              >
                <Heart className="w-4 h-4" style={{ fill: isFollowing ? pc : "none" }} />
                <span className="text-sm hidden sm:inline">{isFollowing ? "Following" : "Follow"}</span>
                {followerCount > 0 && <span className="text-xs opacity-60">{followerCount}</span>}
              </button>

              {/* Following preferences dropdown */}
              {showFollowMenu && isFollowing && (
                <div className="absolute top-full right-0 mt-2 bg-[#1a1a1a] border border-white/10 rounded-xl p-2 w-56 z-[100] shadow-xl" data-testid="follow-prefs-panel">
                  <button
                    onClick={handleToggleNotify}
                    className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm transition hover:bg-white/5"
                    style={{ color: notifyEnabled ? '#22C55E' : '#A1A1AA' }}
                    data-testid="follow-notify-toggle"
                  >
                    {notifyEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                    <span>{notifyEnabled ? "Notifications on" : "Notifications off"}</span>
                    <div className="ml-auto w-8 h-4 rounded-full relative" style={{ background: notifyEnabled ? '#22C55E' : '#3f3f46' }}>
                      <div className="absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all" style={{ left: notifyEnabled ? '17px' : '2px' }} />
                    </div>
                  </button>
                  <div className="border-t border-white/5 my-1" />
                  <button
                    onClick={handleUnfollow}
                    disabled={followLoading}
                    className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm transition hover:bg-red-500/10 text-red-400"
                    data-testid="follow-unfollow-btn"
                  >
                    <UserMinus className="w-4 h-4" />
                    <span>Unfollow station</span>
                  </button>
                </div>
              )}

              {/* Email popup for non-logged-in users */}
              {showFollowEmail && !user && !localStorage.getItem("token") && (
                <div className="absolute top-full right-0 mt-2 bg-[#1a1a1a] border border-white/10 rounded-xl p-3 w-64 z-[100] shadow-xl" data-testid="follow-email-popup">
                  <p className="text-white/60 text-xs mb-2">Enter your email to follow:</p>
                  <input type="email" value={followEmail} onChange={e => setFollowEmail(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mb-2 focus:outline-none focus:border-purple-500"
                    placeholder="your@email.com" data-testid="follow-email-input" />
                  <div className="flex gap-2">
                    <button onClick={handleFollow} disabled={!followEmail || followLoading}
                      className="flex-1 bg-purple-500 hover:bg-purple-600 text-white text-xs py-1.5 rounded-lg font-medium disabled:opacity-40"
                      data-testid="follow-email-submit">Follow</button>
                    <button onClick={() => setShowFollowEmail(false)}
                      className="text-white/40 text-xs hover:text-white/60 px-2">Cancel</button>
                  </div>
                </div>
              )}
            </div>

            {/* External Player Download */}
            <div className="relative">
              <button
                onClick={() => setShowPlsMenu(prev => !prev)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition hover:bg-white/10"
                style={{ background: "#121212", border: "1px solid #27272a" }}
                title="Listen in external player"
                data-testid="wl-external-player-btn"
              >
                <Download className="w-4 h-4" style={{ color: sc }} />
              </button>
              {showPlsMenu && (
                <div className="absolute top-full right-0 mt-2 bg-[#1a1a1a] border border-white/10 rounded-xl p-2 w-48 z-[100] shadow-xl" data-testid="pls-menu-dropdown">
                  <p className="text-white/50 text-[10px] px-2 pb-1.5 font-semibold uppercase tracking-wider">Open in External Player</p>
                  <a href={`${BACKEND_URL}/api/playlist/pls/${slug}`} download={`${slug}.pls`}
                    onClick={() => setShowPlsMenu(false)}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-white transition hover:bg-white/5"
                    data-testid="pls-menu-pls">
                    <Download className="w-3.5 h-3.5" style={{ color: pc }} />
                    Download .PLS
                  </a>
                  <a href={`${BACKEND_URL}/api/playlist/m3u/${slug}`} download={`${slug}.m3u`}
                    onClick={() => setShowPlsMenu(false)}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-white transition hover:bg-white/5"
                    data-testid="pls-menu-m3u">
                    <Download className="w-3.5 h-3.5" style={{ color: sc }} />
                    Download .M3U
                  </a>
                  <p className="text-[9px] px-2 pt-1.5" style={{ color: "#52525B" }}>iTunes, VLC, Winamp, etc.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Social links row */}
        {(() => {
          const socialLinks = [
            { key: 'social_instagram', icon: (
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            ), url: config.social_instagram, label: 'Instagram' },
            { key: 'social_twitter', icon: (
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            ), url: config.social_twitter, label: 'X / Twitter' },
            { key: 'social_facebook', icon: (
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            ), url: config.social_facebook, label: 'Facebook' },
            { key: 'social_youtube', icon: (
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            ), url: config.social_youtube, label: 'YouTube' },
            { key: 'social_tiktok', icon: (
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
            ), url: config.social_tiktok, label: 'TikTok' },
            { key: 'social_soundcloud', icon: (
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.05-.1-.1-.1zm-.899.828c-.06 0-.091.037-.104.094L0 14.479l.172 1.282c.013.06.045.094.104.094.057 0 .09-.037.104-.094l.2-1.282-.2-1.332c-.014-.057-.047-.094-.104-.094zm1.816-1.46c-.074 0-.121.063-.127.13l-.21 2.756.21 2.695c.006.064.053.13.127.13.073 0 .12-.066.127-.13l.24-2.695-.24-2.756c-.007-.067-.054-.13-.127-.13zm.91-.471c-.089 0-.141.076-.145.153l-.193 3.227.193 3.117c.004.077.056.153.145.153.088 0 .139-.076.144-.153l.213-3.117-.213-3.227c-.005-.077-.056-.153-.144-.153zm.912-.376c-.103 0-.163.09-.166.178l-.176 3.603.176 3.444c.003.088.063.178.166.178.102 0 .163-.09.165-.178l.196-3.444-.196-3.603c-.002-.088-.063-.178-.165-.178zm.936-.429c-.12 0-.183.103-.185.205l-.159 4.032.159 3.77c.002.1.065.203.185.203.118 0 .183-.103.184-.203l.178-3.77-.178-4.032c-.001-.102-.066-.205-.184-.205zm.945-.089c-.131 0-.203.116-.204.227l-.143 4.121.143 3.83c.001.113.073.227.204.227.13 0 .203-.114.204-.227l.16-3.83-.16-4.121c-.001-.111-.074-.227-.204-.227zm.944.024c-.147 0-.224.13-.225.252l-.128 4.097.128 3.858c.001.122.078.251.225.251.147 0 .223-.129.224-.251l.143-3.858-.143-4.097c-.001-.122-.077-.252-.224-.252zm.958-.192c-.161 0-.244.141-.245.272l-.112 4.289.112 3.875c.001.133.084.272.245.272.159 0 .243-.139.245-.272l.124-3.875-.124-4.289c-.002-.131-.086-.272-.245-.272zm.964-.124c-.18 0-.264.155-.264.296l-.1 4.413.1 3.898c0 .143.084.296.264.296.178 0 .264-.153.264-.296l.11-3.898-.11-4.413c0-.141-.086-.296-.264-.296zm.95.168c-.193 0-.283.168-.283.315l-.085 3.93.085 3.908c0 .154.09.315.283.315.192 0 .283-.161.284-.315l.095-3.908-.095-3.93c-.001-.147-.092-.315-.284-.315zm1.99-1.594c-.12 0-.217.047-.3.128a.496.496 0 00-.13.31l-.07 5.086.07 3.86a.446.446 0 00.13.313c.083.082.18.125.3.125.118 0 .218-.043.3-.125a.454.454 0 00.132-.313l.078-3.86-.078-5.086a.496.496 0 00-.132-.31.407.407 0 00-.3-.128zm-1.028 1.63c-.19 0-.281.168-.283.312l-.088 3.46.088 3.914c.002.152.093.312.283.312.189 0 .279-.16.281-.312l.098-3.914-.098-3.46c-.002-.144-.092-.312-.281-.312zm3.09-2.056c-.217 0-.398.08-.548.24a.794.794 0 00-.222.557l-.056 5.203.056 3.835a.8.8 0 00.222.56c.15.159.33.239.548.239.217 0 .396-.08.548-.24a.783.783 0 00.225-.559l.001-.049.06-3.786-.061-5.204a.79.79 0 00-.226-.557.752.752 0 00-.547-.24zm1.04.086c-.237 0-.435.088-.6.264a.865.865 0 00-.246.612l-.048 5.084.048 3.81c.002.244.088.45.246.615.165.174.363.26.6.26a.84.84 0 00.6-.26.857.857 0 00.249-.615l.054-3.81-.054-5.084a.865.865 0 00-.25-.612.836.836 0 00-.599-.264zm1.046.237c-.26 0-.476.096-.65.29a.93.93 0 00-.267.664l-.04 4.893.04 3.778c.003.27.098.492.267.669.174.193.39.287.65.287.258 0 .476-.094.65-.287a.94.94 0 00.273-.669l.043-3.778-.044-4.893a.93.93 0 00-.272-.664c-.174-.194-.392-.29-.65-.29zm5.087 1.69c-.328 0-.643.055-.94.164a5.09 5.09 0 00-3.088-3.349 5.067 5.067 0 00-1.717-.3c-.23 0-.38.126-.38.323v8.035c.002.19.159.35.348.367h5.777a2.724 2.724 0 002.71-2.727 2.722 2.722 0 00-2.71-2.513z"/></svg>
            ), url: config.social_soundcloud, label: 'SoundCloud' },
            { key: 'social_spotify', icon: (
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
            ), url: config.social_spotify, label: 'Spotify' },
            { key: 'social_website', icon: <ExternalLink className="w-4 h-4" />, url: config.social_website, label: 'Website' },
          ].filter(s => s.url);
          if (socialLinks.length === 0) return null;
          return (
            <div className="flex items-center gap-1 mt-3 flex-wrap" data-testid="wl-social-links">
              {socialLinks.map(s => (
                <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" title={s.label}
                  className="p-2 rounded-lg transition-all duration-200 hover:scale-110"
                  style={{ color: '#A1A1AA', background: 'transparent' }}
                  onMouseEnter={e => { e.currentTarget.style.color = sc; e.currentTarget.style.background = `${sc}15`; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#A1A1AA'; e.currentTarget.style.background = 'transparent'; }}
                  data-testid={`wl-social-${s.key}`}>
                  {s.icon}
                </a>
              ))}
            </div>
          );
        })()}
      </header>

      <audio ref={audioRef} src={config.stream_url} crossOrigin="anonymous" preload="none"
        onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} />
      <audio ref={onDemandAudioRef} onEnded={() => { clearTimeout(onDemandTimerRef.current); clearInterval(onDemandCountdownRef.current); setOnDemandPlaying(null); setOnDemandTimeLeft(0); if (audioRef.current) { if (audioBusHandle.current) audioBusHandle.current.claim(); audioRef.current.play().catch(() => {}); setIsPlaying(true); } }} />

      {/* On-Demand Overlay */}
      {showOnDemand && (
        <div className="relative z-20 border-b" style={{ borderColor: '#27272a', background: '#0a0a0f' }}>
          <div className="max-w-4xl mx-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Disc3 className="w-6 h-6" style={{ color: pc }} />
                <h2 className="text-white font-bold text-xl" style={{ fontFamily: "Teko, sans-serif", fontSize: "1.5rem" }}>On-Demand Library</h2>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${pc}20`, color: pc }}>{onDemandTracks.length} tracks</span>
              </div>
              <button onClick={() => setShowOnDemand(false)} className="p-2 rounded-lg hover:bg-white/10 transition" style={{ color: '#71717A' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#71717A' }} />
              <input
                type="text"
                placeholder="Search by title, artist, or album..."
                value={onDemandSearch}
                onChange={(e) => { setOnDemandSearch(e.target.value); fetchOnDemand(e.target.value); }}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl text-white text-sm focus:outline-none transition"
                style={{ background: '#18181b', border: `1px solid ${onDemandSearch ? pc : '#27272a'}` }}
                data-testid="wl-ondemand-search"
              />
            </div>
            {/* Now Playing On-Demand Bar */}
            {onDemandPlaying && (
              <div className="flex items-center gap-3 p-3 rounded-xl mb-4" style={{ background: `${pc}10`, border: `1px solid ${pc}30` }}>
                <img src={onDemandPlaying.media?.art} alt="" className="w-10 h-10 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{onDemandPlaying.media?.title}</p>
                  <p className="text-xs truncate" style={{ color: '#A1A1AA' }}>{onDemandPlaying.media?.artist}</p>
                </div>
                {onDemandTimeLeft > 0 && (
                  <span className="text-xs font-mono tabular-nums px-2 py-1 rounded-full flex-shrink-0" style={{ background: `${pc}20`, color: pc }} data-testid="wl-ondemand-countdown">
                    {onDemandTimeLeft}s
                  </span>
                )}
                <button onClick={stopOnDemand} className="p-2 rounded-lg hover:bg-white/10" style={{ color: pc }} title="Stop">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {/* Track List */}
            <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: `${pc}40 transparent` }}>
              {onDemandLoading ? (
                <div className="text-center py-8"><Disc3 className="w-8 h-8 animate-spin mx-auto mb-2" style={{ color: pc }} /><p style={{ color: '#71717A' }} className="text-sm">Loading tracks...</p></div>
              ) : onDemandTracks.length === 0 ? (
                <div className="text-center py-8"><Music className="w-8 h-8 mx-auto mb-2" style={{ color: '#3f3f46' }} /><p style={{ color: '#71717A' }} className="text-sm">{onDemandSearch ? 'No tracks match your search' : 'No on-demand tracks available'}</p></div>
              ) : (
                onDemandTracks.map((track) => {
                  const isActive = onDemandPlaying?.track_id === track.track_id;
                  return (
                    <button
                      key={track.track_id}
                      onClick={() => playOnDemandTrack(track)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left group"
                      style={{ background: isActive ? `${pc}15` : 'transparent', border: `1px solid ${isActive ? `${pc}30` : 'transparent'}` }}
                      data-testid={`wl-ondemand-track-${track.track_id}`}
                    >
                      <div className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={track.media?.art} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition">
                          {isActive ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white" />}
                        </div>
                        {isActive && (
                          <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: pc }}>
                            <div className="h-full animate-pulse" style={{ background: pc, width: '60%' }} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isActive ? '' : 'text-white'}`} style={isActive ? { color: pc } : {}}>{track.media?.title}</p>
                        <p className="text-xs truncate" style={{ color: '#A1A1AA' }}>{track.media?.artist}</p>
                      </div>
                      <div className="hidden sm:block text-right flex-shrink-0">
                        <p className="text-xs truncate max-w-[120px]" style={{ color: '#52525b' }}>{track.media?.album}</p>
                        {track.media?.genre && <p className="text-xs" style={{ color: '#3f3f46' }}>{track.media.genre}</p>}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 flex flex-col lg:flex-row" style={{ minHeight: "calc(100vh - 65px - 32px)" }}>
        {/* ===== LEFT COLUMN - Main Player ===== */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-[900px] mx-auto">

          {/* Live Video */}
          {config.show_video && (
            <div className="glass rounded-2xl p-4 mb-6 w-fit mx-auto" style={{ maxWidth: "100%" }} data-testid="wl-video-section">
              <div className="flex items-center gap-2 mb-3">
                <Tv className="w-5 h-5" style={{ color: pc }} />
                <h3 className="font-bold text-white" style={{ fontFamily: "Teko, sans-serif", fontSize: "1.2rem" }}>Live Video</h3>
              </div>
              {config.video_url ? (
                <div className="overflow-hidden rounded-xl" style={{ width: "576px", maxWidth: "100%", aspectRatio: "576/378" }}>
                  <SmartVideoPlayer url={config.video_url} pc={pc} />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-xl" style={{ width: "576px", maxWidth: "100%", aspectRatio: "576/378", background: "#1E1E1E" }}>
                  <Radio className="w-10 h-10 mb-2" style={{ color: "#A1A1AA" }} />
                  <p className="text-sm" style={{ color: "#A1A1AA" }}>DJ is not streaming video</p>
                  <p className="text-xs" style={{ color: "#71717A" }}>Enjoying audio only</p>
                </div>
              )}
            </div>
          )}

          {/* Album Art — centered below video */}
          <div className="flex flex-col items-center mb-4" data-testid="wl-song-info">
            {config.show_album_art && (
              <div className="mb-4">
                {song.art ? (
                  <img src={song.art} alt={song.title} data-testid="wl-album-art"
                    className="w-56 h-56 lg:w-64 lg:h-64 rounded-2xl object-cover shadow-2xl"
                    style={{ boxShadow: `0 12px 40px ${pc}25` }} />
                ) : (
                  <div className="w-56 h-56 lg:w-64 lg:h-64 rounded-2xl flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${pc}30, ${sc}30)` }}>
                    <Music className="w-16 h-16 text-white/30" />
                  </div>
                )}
              </div>
            )}

            {/* Song Metadata — centered below album art */}
            <div className="text-center max-w-md">
              {song.genre && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white mb-3"
                  style={{ background: `${pc}40` }}>
                  <Headphones className="w-3 h-3" /> {song.genre?.split(";")[0] || "Music"}
                </span>
              )}
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-1"
                style={{ fontFamily: "Teko, sans-serif" }}>
                {song.title || "Unknown Track"}
              </h2>
              <div className="space-y-1 text-sm" style={{ color: "#A1A1AA" }}>
                <p className="flex items-center justify-center gap-1.5">
                  <User className="w-4 h-4 flex-shrink-0" style={{ color: pc }} /> {song.artist || "Unknown Artist"}
                </p>
                {song.album && (
                  <p className="flex items-center justify-center gap-1.5">
                    <Music className="w-4 h-4 flex-shrink-0" style={{ color: pc }} /> {song.album}
                  </p>
                )}
                {!isLiveDJ && duration > 0 && (
                  <p className="flex items-center justify-center gap-2 mt-2 font-mono tabular-nums" data-testid="wl-song-timer">
                    <Clock className="w-4 h-4 flex-shrink-0" style={{ color: pc }} />
                    <span style={{ color: pc }}>{formatTime(elapsed)}</span>
                    <span className="text-white/20">/</span>
                    <span>{formatTime(duration)}</span>
                    <span className="text-white/20">|</span>
                    <span className="text-xs">-{formatTime(Math.max(0, duration - elapsed))}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Progress Bar — h-2, rounded, gradient, same width as video */}
          <div className="mx-auto space-y-2" style={{ width: "576px", maxWidth: "100%" }}>
            <div className="relative h-2 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.15)" }}>
              <div className="h-full rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${Math.min(100, (elapsed / duration) * 100)}%`, background: pc }} />
            </div>
            <div className="flex justify-between text-xs" style={{ color: "#A1A1AA" }}>
              <span>{formatTime(elapsed)}</span>
              <span>-{formatTime(Math.max(0, duration - elapsed))}</span>
            </div>
          </div>

          {/* FFT Frequency Visualizer */}
          {config.show_visualizer && (
            <div className="my-6 mx-auto" style={{ width: "576px", maxWidth: "100%" }} data-testid="wl-visualizer">
              <FFTVisualizer
                isPlaying={isPlaying}
                analyzerRef={analyzerRef}
                pc={pc} sc={sc}
                maxWidth={576}
              />
            </div>
          )}

          {/* Play Button + Volume */}
          <div className="flex flex-col items-center gap-5">
            <button onClick={togglePlay} data-testid="wl-play-btn"
              className="flex-shrink-0 rounded-full flex items-center justify-center transition-colors duration-300 hover:brightness-110"
              style={{
                width: "96px",
                height: "96px",
                minWidth: "96px",
                minHeight: "96px",
                maxWidth: "96px",
                maxHeight: "96px",
                background: pc,
                boxShadow: isPlaying ? `0 0 30px ${pc}50` : "0 8px 20px rgba(0,0,0,0.3)"
              }}>
              {isBuffering && isPlaying ? (
                <RefreshCw className="text-white animate-spin" style={{ width: "32px", height: "32px" }} />
              ) : isPlaying ? (
                <Pause className="text-white" style={{ width: "36px", height: "36px" }} fill="white" />
              ) : (
                <Play className="text-white" style={{ width: "36px", height: "36px", marginLeft: "4px" }} fill="white" />
              )}
            </button>
            <div className="flex items-center gap-3 w-full max-w-xs">
              <button onClick={() => { const newVol = volume > 0 ? 0 : 0.8; setVolume(newVol); if (audioRef.current) audioRef.current.volume = newVol; }}
                className="flex-shrink-0 hover:opacity-80 transition" data-testid="wl-volume-mute-btn">
                <Volume2 className="w-5 h-5" style={{ color: volume === 0 ? '#ef4444' : pc }} />
              </button>
              <div className="flex-1 relative h-8 flex items-center">
                <div className="absolute left-0 right-0 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.15)' }} />
                <div className="absolute left-0 h-1.5 rounded-full" style={{ width: `${Math.round(volume * 100)}%`, background: pc }} />
                <input type="range" min="0" max="100" value={Math.round(volume * 100)}
                  onChange={(e) => {
                    const v = parseInt(e.target.value) / 100;
                    setVolume(v);
                    if (audioRef.current) audioRef.current.volume = v;
                  }}
                  className="absolute left-0 w-full cursor-pointer"
                  style={{ WebkitAppearance: "none", appearance: "none", background: "transparent", height: "32px", margin: 0 }}
                  data-testid="wl-volume-slider"
                />
              </div>
            </div>

            {/* PLS/M3U downloads removed — available in sidebar menu */}
          </div>
          </div>
        </div>

        {/* ===== RIGHT COLUMN - Sidebar ===== */}
        <div className="lg:w-[420px] xl:w-[460px] border-l flex flex-col" style={{ borderColor: "#27272a", background: "#0a0a0a" }}>

          {/* Up Next + Clock */}
          {config.show_up_next && (
            <div className="glass p-4 m-3 rounded-2xl" data-testid="wl-up-next">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SkipForward className="w-5 h-5" style={{ color: sc }} />
                  <h3 className="font-bold text-white" style={{ fontFamily: "Teko, sans-serif", fontSize: "1.2rem" }}>Up Next</h3>
                </div>
                <div className="flex items-center gap-3">
                  {isLiveDJ ? (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
                      style={{ background: '#22c55e20', color: '#22c55e', border: '1px solid #22c55e40' }}
                      data-testid="wl-live-badge">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                      </span>
                      Live
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
                      style={{ background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440' }}
                      data-testid="wl-offline-badge">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      Offline
                    </span>
                  )}
                  {config.show_clock && currentTime && (
                    <div className="flex items-center gap-1.5 text-sm" style={{ color: "#A1A1AA", fontVariantNumeric: 'tabular-nums', minWidth: '120px', justifyContent: 'flex-end' }}>
                      <Clock className="w-4 h-4 flex-shrink-0" /> {currentTime}
                    </div>
                  )}
                </div>
              </div>
              {isLiveDJ && (
                <div className="flex items-center gap-3 mb-3 pb-3" style={{ borderBottom: '1px solid #27272a' }} data-testid="wl-live-dj-info">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#22c55e15', border: '1px solid #22c55e30' }}>
                    <Headphones className="w-6 h-6" style={{ color: '#22c55e' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm" data-testid="wl-dj-name">{liveInfo.streamer_name || "Live DJ"}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs" style={{ color: '#22c55e' }}>Broadcasting Live</p>
                      {liveElapsed && (
                        <span className="text-xs font-mono tabular-nums px-1.5 py-0.5 rounded-md" style={{ background: '#22c55e15', color: '#22c55e' }} data-testid="wl-live-duration">
                          {liveElapsed}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {playingNext?.song ? (
                <div className="flex items-center gap-3">
                  {playingNext.song?.art ? (
                    <img src={playingNext.song.art} alt="" className="w-16 h-16 rounded-xl object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ background: `${pc}20` }}>
                      <Music className="w-8 h-8 text-white/30" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold truncate">{playingNext.song?.title || "Next Track"}</p>
                    <p className="text-sm truncate" style={{ color: "#A1A1AA" }}>{playingNext.song?.artist || ""}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {playingNext.playlist && (
                        <span className="inline-block text-xs px-2 py-0.5 rounded-full" style={{ background: `${sc}30`, color: sc }}>
                          {playingNext.playlist}
                        </span>
                      )}
                      {playingNext.duration > 0 && (
                        <div className="flex items-center gap-1 text-xs" style={{ color: "#A1A1AA" }}>
                          <Clock className="w-3 h-3" /> {formatTime(playingNext.duration)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm" style={{ color: "#A1A1AA" }}>Loading next track...</p>
              )}
            </div>
          )}

          {/* Tab Navigation */}
          {tabs.length > 0 && (
            <div className="flex border-b px-3" style={{ borderColor: "#27272a" }}>
              {tabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`wl-tab-${tab.id}`}
                  className="flex-1 flex flex-col items-center gap-1 py-3 text-xs transition-all"
                  style={{ color: activeTab === tab.id ? pc : "#A1A1AA", borderBottom: activeTab === tab.id ? `2px solid ${pc}` : "2px solid transparent" }}>
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-3">
            {/* Weather */}
            {config.show_weather && weather && (
              <div className="glass rounded-2xl p-4 mb-3" data-testid="wl-weather">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CloudRain className="w-5 h-5" style={{ color: pc }} />
                    <h3 className="font-bold text-white" style={{ fontFamily: "Teko, sans-serif", fontSize: "1.1rem" }}>
                      {weather.city || config.weather_city} Weather
                    </h3>
                  </div>
                  <span className="text-2xl font-bold text-white">{weather.temp_f}°F</span>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: "#A1A1AA" }}>
                  <span>Feels like {weather.feels_like_f}°F</span>
                  <span>Humidity {weather.humidity}%</span>
                  <span>Wind {weather.wind_mph} mph</span>
                </div>
                <p className="text-xs mt-1" style={{ color: "#A1A1AA" }}>{weather.description}</p>
              </div>
            )}

            {/* History Tab */}
            {activeTab === "history" && config.show_recently_played && (
              <div className="glass rounded-2xl p-4" data-testid="wl-history">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5" style={{ color: pc }} />
                  <h3 className="font-bold text-white" style={{ fontFamily: "Teko, sans-serif", fontSize: "1.2rem" }}>Recently Played</h3>
                </div>
                <div className="space-y-3">
                  {songHistory.slice(0, 8).map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition">
                      {item.song?.art ? (
                        <img src={item.song.art} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: "#1E1E1E" }}>
                          <Music className="w-6 h-6 text-white/30" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{item.song?.title || "Unknown"}</p>
                        <p className="text-xs truncate" style={{ color: "#A1A1AA" }}>{item.song?.artist || "Unknown"}</p>
                      </div>
                      <span className="text-xs whitespace-nowrap" style={{ color: "#71717A" }}>
                        {item.played_at ? timeAgo(item.played_at) : ""}
                      </span>
                    </div>
                  ))}
                  {songHistory.length === 0 && (
                    <p className="text-center text-sm py-4" style={{ color: "#71717A" }}>No history yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Request Tab */}
            {activeTab === "request" && config.show_requests && (
              <div className="glass rounded-2xl p-4" data-testid="wl-requests">
                <div className="flex items-center gap-2 mb-4">
                  <Music className="w-5 h-5" style={{ color: pc }} />
                  <h3 className="font-bold text-white" style={{ fontFamily: "Teko, sans-serif", fontSize: "1.2rem" }}>Request a Song</h3>
                </div>
                <div className="space-y-3">
                  <input type="text" value={requestText} onChange={(e) => setRequestText(e.target.value)}
                    placeholder="Song name or artist..." data-testid="wl-request-input"
                    className="w-full rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none transition"
                    style={{ background: "#1E1E1E", border: "1px solid #27272a" }} />
                  <button onClick={async () => {
                    if (requestText.trim()) {
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/whitelabel/player/${slug}/request`, {
                          method: "POST", headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ song: requestText, type: "request" }),
                        });
                        if (res.ok) { setRequestSuccess("Request submitted!"); setRequestText(""); }
                        else { setRequestSuccess("Failed to submit request"); }
                      } catch { setRequestSuccess("Failed to submit request"); }
                      setTimeout(() => setRequestSuccess(""), 3000);
                    }
                  }} data-testid="wl-request-submit"
                    className="w-full py-3 rounded-xl text-white font-semibold text-sm transition hover:opacity-90"
                    style={{ background: `linear-gradient(135deg, ${pc}, ${sc})` }}>
                    Submit Request
                  </button>
                  {requestSuccess && <p className="text-sm text-center" style={{ color: sc }}>{requestSuccess}</p>}
                </div>
              </div>
            )}

            {/* Dedicate Tab */}
            {activeTab === "dedicate" && config.show_dedicate && (
              <div className="glass rounded-2xl p-4" data-testid="wl-dedicate">
                <div className="flex items-center gap-2 mb-4">
                  <Heart className="w-5 h-5" style={{ color: pc }} />
                  <h3 className="font-bold text-white" style={{ fontFamily: "Teko, sans-serif", fontSize: "1.2rem" }}>Dedicate a Song</h3>
                </div>
                <div className="space-y-3">
                  <input type="text" value={dedicateText} onChange={(e) => setDedicateText(e.target.value)}
                    placeholder="Song name..."
                    className="w-full rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none transition"
                    style={{ background: "#1E1E1E", border: "1px solid #27272a" }} />
                  <input type="text" value={dedicateTo} onChange={(e) => setDedicateTo(e.target.value)}
                    placeholder="Dedicate to..."
                    className="w-full rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none transition"
                    style={{ background: "#1E1E1E", border: "1px solid #27272a" }} />
                  <button onClick={async () => {
                    if (dedicateText.trim()) {
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/whitelabel/player/${slug}/request`, {
                          method: "POST", headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ song: dedicateText, dedicated_to: dedicateTo, type: "dedication" }),
                        });
                        if (res.ok) { setRequestSuccess("Dedication sent!"); setDedicateText(""); setDedicateTo(""); }
                        else { setRequestSuccess("Failed to send dedication"); }
                      } catch { setRequestSuccess("Failed to send dedication"); }
                      setTimeout(() => setRequestSuccess(""), 3000);
                    }
                  }}
                    className="w-full py-3 rounded-xl text-white font-semibold text-sm transition hover:opacity-90"
                    style={{ background: `linear-gradient(135deg, ${pc}, ${sc})` }}>
                    Send Dedication
                  </button>
                  {requestSuccess && <p className="text-sm text-center" style={{ color: sc }}>{requestSuccess}</p>}
                </div>
              </div>
            )}

            {/* Share Tab */}
            {activeTab === "share" && config.show_share && (
              <div className="glass rounded-2xl p-4" data-testid="wl-share">
                <div className="flex items-center gap-2 mb-4">
                  <Share2 className="w-5 h-5" style={{ color: pc }} />
                  <h3 className="font-bold text-white" style={{ fontFamily: "Teko, sans-serif", fontSize: "1.2rem" }}>Share</h3>
                </div>
                <p className="text-sm mb-4" style={{ color: "#A1A1AA" }}>Share what you're listening to</p>
                <div className="space-y-2">
                  <button onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    setRequestSuccess("Link copied!");
                    setTimeout(() => setRequestSuccess(""), 2000);
                  }}
                    className="w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 transition hover:opacity-90"
                    style={{ background: "#1E1E1E", border: "1px solid #27272a" }}>
                    <Link2 className="w-4 h-4" /> Copy Player Link
                  </button>
                  <a href={`https://twitter.com/intent/tweet?text=Listening to ${encodeURIComponent(song.title || "Live Radio")} on ${encodeURIComponent(config.player_name || "Live Radio")}&url=${encodeURIComponent(window.location.href)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 transition hover:opacity-90"
                    style={{ background: "#1E1E1E", border: "1px solid #27272a" }}>
                    Share on X/Twitter
                  </a>
                </div>

                {/* External Player Downloads */}
                <div className="mt-4 pt-4" style={{ borderTop: "1px solid #27272a" }}>
                  <p className="text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: "#A1A1AA" }}>
                    <Headphones className="w-4 h-4" /> Listen in External Player
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <a href={`${BACKEND_URL}/api/playlist/pls/${slug}`}
                      download={`${slug}.pls`}
                      className="py-2.5 rounded-xl text-white font-semibold text-xs flex items-center justify-center gap-2 transition hover:opacity-90"
                      style={{ background: `${pc}15`, border: `1px solid ${pc}40` }}
                      data-testid="download-pls-btn">
                      <Download className="w-3.5 h-3.5" style={{ color: pc }} /> .PLS File
                    </a>
                    <a href={`${BACKEND_URL}/api/playlist/m3u/${slug}`}
                      download={`${slug}.m3u`}
                      className="py-2.5 rounded-xl text-white font-semibold text-xs flex items-center justify-center gap-2 transition hover:opacity-90"
                      style={{ background: `${sc}15`, border: `1px solid ${sc}40` }}
                      data-testid="download-m3u-btn">
                      <Download className="w-3.5 h-3.5" style={{ color: sc }} /> .M3U File
                    </a>
                  </div>
                  <p className="text-[10px] mt-2 text-center" style={{ color: "#52525B" }}>
                    Open in iTunes, VLC, Winamp, or any media player
                  </p>
                </div>
                {requestSuccess && <p className="text-sm text-center mt-2" style={{ color: sc }}>{requestSuccess}</p>}
              </div>
            )}

            {/* Top Fans Tab */}
            {activeTab === "topfans" && config.show_top_fans && (
              <div className="glass rounded-2xl p-4" data-testid="wl-topfans">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" style={{ color: pc }} />
                    <h3 className="font-bold text-white" style={{ fontFamily: "Teko, sans-serif", fontSize: "1.2rem" }}>Top Fans</h3>
                  </div>
                  {user && myPoints.points > 0 && (
                    <div className="text-xs px-2.5 py-1 rounded-full" style={{ background: `${pc}20`, color: pc }}>
                      {myPoints.points} pts {myPoints.rank && `• #${myPoints.rank}`}
                    </div>
                  )}
                </div>
                {leaderboard.length === 0 ? (
                  <p className="text-center text-sm py-6" style={{ color: "#71717A" }}>
                    {user ? 'Keep listening to earn points and climb the leaderboard!' : 'Sign in and listen to earn points!'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {leaderboard.map((fan, i) => (
                      <div key={fan.user_id} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: i === 0 ? `${pc}10` : 'transparent' }}>
                        <span className="w-6 text-center font-bold text-sm" style={{ color: i < 3 ? pc : '#71717A' }}>
                          {i === 0 ? '👑' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                        </span>
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: `${pc}20`, color: pc }}>
                          {(fan.user_name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{fan.user_name}</p>
                        </div>
                        <div className="text-xs font-medium" style={{ color: '#A1A1AA' }}>
                          {fan.points} pts
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Live Chat */}
          {config.show_chat && (
            <div className="border-t flex flex-col" style={{ borderColor: "#27272a", minHeight: "250px" }}>
              <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: "#27272a" }}>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" style={{ color: pc }} />
                  <h3 className="font-bold text-white" style={{ fontFamily: "Teko, sans-serif", fontSize: "1.1rem" }}>Live Chat</h3>
                </div>
                <div className="flex items-center gap-1.5 text-xs" style={{ color: "#A1A1AA" }}>
                  <Users className="w-3.5 h-3.5" /> {chatMessages.length} Active
                </div>
              </div>
              <div className="flex-1 p-3 overflow-y-auto space-y-2 max-h-[200px]">
                {chatMessages.length === 0 ? (
                  <p className="text-center text-xs py-4" style={{ color: "#71717A" }}>No messages yet</p>
                ) : (
                  chatMessages.map((msg, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: "#1E1E1E" }}>
                        <User className="w-4 h-4" style={{ color: "#A1A1AA" }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white text-xs font-semibold">{msg.user}</span>
                          <span className="text-xs" style={{ color: "#71717A" }}>{msg.time}</span>
                        </div>
                        <p className="text-white text-sm">{msg.text}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="p-3 border-t" style={{ borderColor: "#27272a" }}>
                {user ? (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    if (chatInput.trim()) {
                      setChatMessages([...chatMessages, {
                        user: user.name || user.email?.split("@")[0] || "Listener",
                        text: chatInput,
                        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                      }]);
                      setChatInput("");
                    }
                  }} className="flex gap-2">
                    <input type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Type a message..." data-testid="wl-chat-input"
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-white/30 focus:outline-none" />
                    <button type="submit" disabled={!chatInput.trim()} data-testid="wl-chat-send"
                      className="px-4 py-2.5 rounded-xl text-white font-semibold text-sm transition disabled:opacity-40"
                      style={{ background: `linear-gradient(135deg, ${pc}, ${sc})` }}>
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                ) : (
                  <button onClick={() => setShowChatLogin(true)} data-testid="wl-chat-signin"
                    className="w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 transition"
                    style={{ background: `linear-gradient(135deg, ${pc}, ${sc})` }}>
                    <LogIn className="w-4 h-4" /> Sign in to chat
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Sponsor */}
          {config.show_sponsor && config.sponsor_name && (
            <div className="p-3" data-testid="wl-sponsor">
              <div className="glass rounded-2xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold text-sm">{config.sponsor_name}</p>
                  {config.sponsor_tagline && <p className="text-xs" style={{ color: "#A1A1AA" }}>{config.sponsor_tagline}</p>}
                </div>
                {config.sponsor_link && (
                  <a href={config.sponsor_link} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-white transition hover:bg-white/10"
                    style={{ border: "1px solid #27272a" }}>
                    Sponsor <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Powered By — Always shown in embed mode */}
          {(config.show_powered_by || isEmbedMode) && (
            <div className="p-3 text-center">
              <a href="https://djsandmcmedia.com" target="_blank" rel="noopener noreferrer"
                className="text-xs transition hover:opacity-80" style={{ color: "#71717A" }}>
                Powered by <span style={{ color: sc }}>DJsandMCMedia™</span>
              </a>
              {isEmbedMode && (
                <div className="mt-1">
                  <a href="https://djsandmcmedia.com/#pricing" target="_blank" rel="noopener noreferrer"
                    className="text-[10px] transition hover:opacity-80" style={{ color: pc }}>
                    Remove branding → Upgrade to White Label
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* News Ticker */}
      {config.show_ticker !== false && <NewsTicker pc={pc} messages={config.ticker_messages} />}

      {/* Chat Login Modal */}
      {showChatLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${pc}dd, ${sc}dd)` }} data-testid="wl-chat-login-modal">
          <div className="w-full max-w-md mx-4 rounded-2xl p-8 border border-white/10" style={{ background: "#18181b" }}>
            <button onClick={() => { setShowChatLogin(false); setChatAuthError(""); }}
              className="flex items-center gap-2 text-white/50 hover:text-white text-sm mb-6 transition" data-testid="wl-chat-login-back">
              <ArrowLeft className="w-4 h-4" /> Back to player
            </button>

            {config.logo_url && (
              <div className="flex justify-center mb-4">
                <img src={config.logo_url} alt="Station" className="w-16 h-16 rounded-full object-cover" />
              </div>
            )}

            <p className="text-center text-white/60 text-sm mb-6">Sign in to chat</p>

            <button onClick={handleChatGoogleSignIn} data-testid="wl-chat-google-btn"
              className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-800 font-medium py-2.5 rounded-xl transition mb-4">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
              <div className="relative flex justify-center text-sm"><span className="px-4 text-white/40" style={{ background: "#18181b" }}>or</span></div>
            </div>

            {chatAuthError && <p className="text-red-400 text-sm text-center mb-3">{chatAuthError}</p>}

            <form onSubmit={handleChatAuth} className="space-y-3">
              {chatAuthMode === "register" && (
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input type="text" value={chatAuthName} onChange={(e) => setChatAuthName(e.target.value)}
                    placeholder="Name" required data-testid="wl-chat-name-input"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-purple-500/50" />
                </div>
              )}
              <div className="relative">
                <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input type="email" value={chatAuthEmail} onChange={(e) => setChatAuthEmail(e.target.value)}
                  placeholder="Email" required data-testid="wl-chat-email-input"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-purple-500/50" />
              </div>
              <div className="relative">
                <Settings className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input type="password" value={chatAuthPassword} onChange={(e) => setChatAuthPassword(e.target.value)}
                  placeholder="Password" required data-testid="wl-chat-password-input"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-purple-500/50" />
              </div>
              <button type="submit" disabled={chatAuthLoading} data-testid="wl-chat-auth-submit"
                className="w-full py-2.5 rounded-xl text-white font-semibold text-sm transition disabled:opacity-50"
                style={{ background: `linear-gradient(135deg, ${pc}, ${sc})` }}>
                {chatAuthLoading ? "Loading..." : chatAuthMode === "login" ? "Sign In" : "Sign Up"}
              </button>
            </form>

            <p className="text-center text-white/40 text-sm mt-4">
              {chatAuthMode === "login" ? (
                <>Don't have an account? <button onClick={() => { setChatAuthMode("register"); setChatAuthError(""); }} className="text-white hover:underline" data-testid="wl-chat-switch-signup">Sign Up</button></>
              ) : (
                <>Already have an account? <button onClick={() => { setChatAuthMode("login"); setChatAuthError(""); }} className="text-white hover:underline" data-testid="wl-chat-switch-login">Sign In</button></>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhiteLabelPlayer;
